import { LayoutGrid, Maximize2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { ExecucaoDaFrente } from '@/features/room-progress/ExecucaoDaFrente';
import { CronogramaHeader } from '@/features/schedule/CronogramaHeader';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';

const SELECT_CLASS =
  'h-11 min-w-0 max-w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-3 text-sm md:h-9 md:max-w-[28rem]';

/** Um pavimento × serviço do cronograma: o que a tela pinta. Vários trechos são um só. */
interface FrenteDoServico {
  readonly key: string;
  readonly towerId: string;
  readonly towerName: string;
  readonly floorId: string;
  readonly floorName: string;
  readonly floorLevel: number;
  readonly activityId: string;
  readonly serviceId: string;
  readonly serviceName: string;
  readonly disciplineName: string;
  readonly unit: string | null;
}

function frentesDosItens(items: readonly ScheduleItemView[]): readonly FrenteDoServico[] {
  const frentes = new Map<string, FrenteDoServico>();
  for (const item of items) {
    if (item.serviceId === null || item.serviceName === null) continue;
    const key = `${item.activityId}|${item.serviceId}`;
    if (frentes.has(key)) continue;
    frentes.set(key, {
      key,
      towerId: item.towerId,
      towerName: item.towerName,
      floorId: item.floorId,
      floorName: item.floorName,
      floorLevel: item.floorLevel,
      activityId: item.activityId,
      serviceId: item.serviceId,
      serviceName: item.serviceName,
      disciplineName: item.disciplineName,
      unit: item.unit,
    });
  }
  return [...frentes.values()].sort(
    (a, b) =>
      a.towerName.localeCompare(b.towerName, 'pt-BR', { numeric: true }) ||
      a.floorLevel - b.floorLevel ||
      a.disciplineName.localeCompare(b.disciplineName, 'pt-BR') ||
      a.serviceName.localeCompare(b.serviceName, 'pt-BR'),
  );
}

/**
 * A execução por cômodo (Fase 34, D14; redesenho de 21/10/2026): uma das três vistas do
 * Cronograma. O pavimento e o serviço escolhidos no topo, e embaixo o número grande, a planta
 * quando o desenho tem contorno e a grade de ladrilhos — cômodo a cômodo, peça a peça.
 *
 * As frentes vêm dos itens do cronograma por mão de obra: só o que tem serviço se executa por
 * cômodo. A frente escolhida fica na URL (`?frente=&servico=`), que é o atalho que o item do
 * cronograma abre. "Marcar na planta" é a ação da vista, no cabeçalho: abre a planta em tela
 * cheia, onde se marca o cômodo e a peça.
 */
export function ExecutionPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canMark = usePermission(PERMISSION.LABOR_CONFIRM);
  const [params, setParams] = useSearchParams();
  const [cheia, setCheia] = useState(false);

  const response = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listScheduleItems.execute({ projectId: activeProjectId }, session),
    [session?.userId, activeProjectId],
  );
  const frentes = useMemo(
    () => (response !== undefined && response.ok ? frentesDosItens(response.value) : []),
    [response],
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver a execução por cômodo."
        icon={<LayoutGrid className="size-8" />}
      />
    );
  }
  if (response === undefined) return <LoadingState label="Carregando a execução…" />;
  if (!response.ok)
    return <ErrorState message={response.error.message} code={response.error.code} />;

  const pedida = frentes.find(
    (frente) =>
      frente.activityId === params.get('frente') && frente.serviceId === params.get('servico'),
  );
  const escolhida = pedida ?? frentes[0];
  const pavimentos = [
    ...new Map(
      frentes.map((frente) => [
        frente.floorId,
        { floorId: frente.floorId, nome: `${frente.towerName} · ${frente.floorName}` },
      ]),
    ).values(),
  ];
  const doPavimento = frentes.filter((frente) => frente.floorId === escolhida?.floorId);

  const escolher = (frente: FrenteDoServico | undefined): void => {
    if (frente === undefined) return;
    setParams({ frente: frente.activityId, servico: frente.serviceId }, { replace: true });
  };

  return (
    <div className="flex flex-col gap-5">
      <CronogramaHeader
        atual="/cronograma/execucao"
        {...(escolhida === undefined
          ? {}
          : {
              actions: (
                <Button variant="primary" onClick={() => setCheia(true)}>
                  <Maximize2 aria-hidden="true" />
                  {canMark ? 'Marcar na planta' : 'Ver a planta'}
                </Button>
              ),
            })}
      />

      {escolhida === undefined ? (
        <EmptyState
          title="Nenhum item com serviço"
          description="A execução por cômodo parte dos itens do cronograma por mão de obra. Importe o cronograma ou gere do quantitativo."
          icon={<LayoutGrid className="size-8" />}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label
              htmlFor="execucao-pavimento"
              className="flex min-w-0 max-w-full items-center gap-2 text-[13px] text-(--color-content-muted)"
            >
              Pavimento
              <select
                id="execucao-pavimento"
                className={SELECT_CLASS}
                value={escolhida.floorId}
                onChange={(event) => {
                  const mesmoServico = frentes.find(
                    (frente) =>
                      frente.floorId === event.target.value &&
                      frente.serviceId === escolhida.serviceId,
                  );
                  escolher(
                    mesmoServico ?? frentes.find((frente) => frente.floorId === event.target.value),
                  );
                }}
              >
                {pavimentos.map((pavimento) => (
                  <option key={pavimento.floorId} value={pavimento.floorId}>
                    {pavimento.nome}
                  </option>
                ))}
              </select>
            </label>
            <label
              htmlFor="execucao-servico"
              className="flex min-w-0 max-w-full items-center gap-2 text-[13px] text-(--color-content-muted)"
            >
              Serviço
              <select
                id="execucao-servico"
                className={SELECT_CLASS}
                value={escolhida.key}
                onChange={(event) =>
                  escolher(doPavimento.find((frente) => frente.key === event.target.value))
                }
              >
                {doPavimento.map((frente) => (
                  <option key={frente.key} value={frente.key}>
                    {frente.disciplineName} · {frente.serviceName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <h2 className="text-[17px] leading-tight font-semibold">
            {escolhida.serviceName} · {escolhida.floorName}
            <span className="mt-0.5 block text-[13px] font-normal text-(--color-content-muted)">
              {escolhida.towerName} · {escolhida.disciplineName}
            </span>
          </h2>

          <ExecucaoDaFrente
            activityId={escolhida.activityId}
            serviceId={escolhida.serviceId}
            label={`${escolhida.serviceName} · ${escolhida.floorName}`}
            unit={escolhida.unit ?? undefined}
            cheia={cheia}
            onCheiaChange={setCheia}
          />

          <p className="max-w-[78ch] text-xs text-(--color-content-muted)">
            O percentual do cômodo é o do último apontamento; a peça fica marcada pelo apontamento
            do dia. Na planta, em tela cheia, marca-se vários de uma vez.
          </p>
        </>
      )}
    </div>
  );
}
